import p2 from 'p2';
import { Texture } from 'pixi.js';

import { Shared } from '../../App';
import { Settings } from '../../AppConfig';
import { SplineDisplay } from '../../display/SplineDisplay';
import { Maths } from '../../utils/Maths';
import { Segment, SegmentPoint } from '../../utils/Segment';
import { Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsComponent } from '../physics';
import { ChunkSpriteData } from '../systems/ChunkSystem';
import { BaseEntity } from './BaseEntity';
import { GroundTopFactory } from './GroundTopEntity';
import { SpriteEntity } from './SpriteEntity';

export interface GroundEntityParams
{
    type: string,
    precision: number,
    thickness: number,
    baseline: number,
    x: number,
    y: number,
    img: string
    collisionGroup: number,
    collisionMask: number,
    maxPrecision: number,
    colinearAngle: number,
    verts: {x: number, y: number, cx: number, cy: number}[]
    props: {
        direction?: 'left'|'right'
        flying?: boolean
    }
}

const GROUND_MAX_PRECISION = 16;
const GROUND_COLLINEAR_ANGLE = 0.005;

export class GroundEntity extends BaseEntity
{
    public spline!: SplineDisplay;
    public params!: GroundEntityParams;
    public physics: PhysicsComponent;
    public slime = false;
    public conveyer = '';
    public flying = false;
    public inverse = false;
    public length = 0;
    public material!: p2.Material;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent as any) as PhysicsComponent;
        this.physics.harmPlayer = true;
        this.physics.culling = true;
        this.physics.ground = true;
    }

    public build(params: GroundEntityParams): void
    {
        this.params = params;
        this.slime = this.params.img === 'ground-slime';
        this.conveyer = this.params.img === 'ground-conveyer' ? this.params.props?.direction || 'right' : '';

        if (this.conveyer)
        {
            this.material = Shared.game.physics.contact.getMaterial({
                name: `conveyer-${this.conveyer}`,
                friction: 4,
                surfaceVelocity: this.conveyer === 'left' ? 1000 : -1000,
            });
        }
        else if (this.slime)
        {
            this.material = Shared.game.physics.contact.getMaterial({
                name: `ground-slime`,
                friction: 0.01,
            });
        }
        else
        {
            this.material = Shared.game.physics.contact.getDefaultGroundMaterial();
        }

        const maxPrecision = params.maxPrecision ?? GROUND_MAX_PRECISION;

        if (maxPrecision && this.params.precision > maxPrecision) this.params.precision = maxPrecision;
        if (this.params.colinearAngle === undefined) this.params.colinearAngle = GROUND_COLLINEAR_ANGLE;

        this.physics.clearParts();
        this.params.x = this.params.x || 0;
        this.params.y = this.params.y || 0;
        this.flying = this.params.props.flying ?? false;
        this.buildBodies(this.params.colinearAngle);
    }

    private buildBodies(colinearAngle: number): void
    {
        const lines = Segment.getLines(this.params.verts);

        this.length = Segment.getLinesLength(lines);

        for (const line of lines)
        {
            const points = Segment.getPointsFromLine(line, this.params.precision, colinearAngle);

            for (let i = 0; i < points.length - 1; i++)
            {
                const pair = [points[i], points[i + 1]];

                this.buildBodyBoxes(pair);
            }
        }
    }

    private buildBodyBoxes(points: SegmentPoint[]): p2.Body
    {
        const a = points[0];
        const b = points[1];
        const w = Maths.distance(a.x, a.y, b.x, b.y);
        const h = 50;
        const angle = -Math.atan2(b.y - a.y, b.x - a.x);

        const mx = ((b.x - a.x) / 2) + (Math.sin(angle) * (h / 2));
        const my = ((a.y - b.y) / 2) - (Math.cos(angle) * (h / 2));

        const body = new p2.Body({
            position: [
                a.x + this.params.x + mx,
                -a.y - this.params.y + my,
            ],
            mass: 0,
            angle,
        });

        const shape = new p2.Box({
            width: w,
            height: h,
        });

        body.addShape(shape);

        shape.collisionGroup = PhysicsCollisionGroup.GROUND;
        shape.collisionMask = PhysicsCollisionGroup.VEHICLE | PhysicsCollisionGroup.SPECIAL;
        // shape.material = this.slime ? PhysicsMaterial.slime : PhysicsMaterial.ground;
        shape.material = this.material;

        body.physicsComponent = this.physics;
        this.physics.bodies.push(body);

        return this.physics.bodies[0];
    }
}

export class GroundFactory
{
    public static match(data: ChunkSpriteData): boolean
    {
        return data.type === 'SegmentEntity' && (data.img.startsWith('ground') || data.img.startsWith('conveyer'));
    }

    public static create(game: Game, params: GroundEntityParams): void
    {
        const ground = game.pool.get(GroundEntity) as GroundEntity;

        ground.build(params);
        ground.flying = !!params.props.flying;
        ground.inverse = params.verts[0].x >= params.verts[params.verts.length - 1].x;
        game.level.addEntity(ground);

        GroundTopFactory.placeTop(game, ground);

        if (Settings.debug)
        {
            this.placeDebugNormalLines(game, ground);
        }

        game.level.segmentsLength += ground.length;
    }

    public static placeDebugNormalLines(game: Game, ground: GroundEntity): void
    {
        const points = Segment.getPointsFromVerts(ground.params.verts, false, 7);

        for (const point of points)
        {
            const line = game.pool.get(SpriteEntity) as SpriteEntity;

            line.build({
                x: point.x + ground.params.x,
                y: point.y + ground.params.y,
                z: 0,
                sx: 10,
                sy: 0.2,
                ax: 0.5,
                ay: 0.5,
                r: point.a,
                alpha: 0.25,
                texture: Texture.WHITE,
            });

            game.level.addEntity(line);
        }
    }
}

